import React, { useEffect, useState } from "react";
import api from "../../services/api";

interface QuickButton {
  id: string;
  label: string;
  product_id: string;
  sellable_format_id: string;
  color: string | null;
  cash_price: number;
  card_price: number;
  product_name: string;
  format_name: string;
}

interface QuickButtonGroup {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
  buttons: QuickButton[];
}

interface QuickButtonsProps {
  onAddItem: (productId: string, formatId: string) => void;
}

const GROUP_COLORS: Record<string, { bg: string; activeBg: string; text: string }> = {
  Slushies: { bg: "#8BB8E815", activeBg: "#8BB8E830", text: "var(--rbw-blue-dark)" },
  "Growlers & Crowlers": { bg: "#2D8A4E15", activeBg: "#2D8A4E30", text: "var(--rbw-success)" },
  "Big Cans": { bg: "#F6BE0015", activeBg: "#F6BE0030", text: "var(--rbw-gold-dark)" },
};

const DEFAULT_GROUP = { bg: "var(--rbw-gray-light)", activeBg: "var(--rbw-gray)", text: "var(--rbw-navy)" };

export default function QuickButtons({ onAddItem }: QuickButtonsProps) {
  const [groups, setGroups] = useState<QuickButtonGroup[]>([]);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);

  useEffect(() => {
    api.get("/quick-buttons").then((r) => {
      setGroups(r.data);
    }).catch(() => {});
  }, []);

  if (groups.length === 0) return null;

  const currentGroup = groups.find((g) => g.id === activeGroup);

  return (
    <div style={{ borderBottom: "1px solid var(--rbw-border)" }}>
      {/* Group tabs */}
      <div className="flex gap-1.5 px-3 pt-2 pb-1">
        {groups.map((group) => {
          const colors = GROUP_COLORS[group.name] || DEFAULT_GROUP;
          const isActive = activeGroup === group.id;
          return (
            <button
              key={group.id}
              onClick={() => setActiveGroup(isActive ? null : group.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all active:scale-95"
              style={{
                background: isActive ? colors.activeBg : colors.bg,
                color: colors.text,
                border: isActive ? `2px solid ${colors.text}` : "2px solid transparent",
              }}
            >
              {group.name}
            </button>
          );
        })}
      </div>

      {/* Buttons grid */}
      {currentGroup && currentGroup.buttons.length > 0 && (
        <div className="flex gap-1.5 px-3 py-2 overflow-x-auto">
          {currentGroup.buttons.map((btn) => (
            <button
              key={btn.id}
              onClick={() => onAddItem(btn.product_id, btn.sellable_format_id)}
              className="flex-shrink-0 px-3 py-2 rounded-lg text-left transition-all active:scale-95 hover:shadow-md"
              style={{
                background: "var(--rbw-bg-card)",
                border: "1px solid var(--rbw-border)",
                minWidth: "120px",
              }}
            >
              <div className="text-xs font-semibold truncate" style={{ color: "var(--rbw-navy)" }}>
                {btn.label}
              </div>
              <div className="flex justify-between items-baseline mt-0.5">
                <span className="text-sm font-bold" style={{ color: "var(--rbw-gold-dark)" }}>
                  ${Number(btn.cash_price).toFixed(2)}
                </span>
                <span className="text-[10px]" style={{ color: "var(--rbw-gray-dark)" }}>
                  card ${Number(btn.card_price).toFixed(2)}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
